import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useProductsContext } from "../../Contexts/SidebarContext";
import { NewCampaignDialog } from "./NewCampaignDialog";
import MarketingMessages from "./MarketingMessages";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import ToastManager from "../../Components/ToastManager";
import { useNavigate } from "react-router-dom";
import SearchBarWithButton from "../BroadcastFBManage/DMComponents/SearchBarWithButton";
import CampaignsList from "../BroadcastFBManage/DMComponents/CampaignsList";

interface Campaign {
  messageCampaignId: string;
  messageCampaignName: string;
  lifetimeBudget: number;
  campaignStatus: boolean;
}

interface ApiResponse {
  statusCode: string;
  messageCampaignDetails?: Campaign[];
  Message?: string;
}

const CampaignsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { sideBar } = useProductsContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [routeToMM, setRouteToMM] = useState(false);
  const [routeToCampaigns, setRouteToCampaigns] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId, adAccountName, adAccountCurrency } =
    readJson("fbbAdAccountDetail") || "{}";

  const filteredCampaigns = useMemo(
    () =>
      campaigns.filter((campaign) =>
        campaign.messageCampaignName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [campaigns, searchTerm]
  );

  const onSuccessCall = useCallback((data: string) => {
    const respData = CommonUtil.parseData(data) as ApiResponse;
    if (respData) {
      if (respData.statusCode === "BM0000" && respData.messageCampaignDetails) {
        setCampaigns(respData.messageCampaignDetails);
      } else {
        ToastManager.showToast("No message campaigns are available.", "Error");
      }
    }
  }, []);

  const onErrorCall = useCallback(() => {
    ToastManager.showToast("Failed to fetch campaigns.", "Error");
  }, []);

  const fetchCampaigns = useCallback(() => {
    if (!pageId || !adAccountId) {
      return;
    }

    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastMessageTemplates/getMessageCampaigns",
        "",
        pageDataReq,
        "GET",
        true,
        onSuccessCall,
        onErrorCall
      );
    } catch (error) {
      ToastManager.showToast("Error fetching campaigns.", "Error");
    }
  }, [pageId, adAccountId, onSuccessCall, onErrorCall]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleOpenCreate = useCallback(() => {
    setOpenDialog(true);
    setEditCampaign(null);
  }, []);

  const handleRouteToMM = useCallback((campaign: Campaign) => {
    localStorage.setItem("selectedCampaign", JSON.stringify(campaign));
    setRouteToCampaigns(false);
    setRouteToMM(true);
  }, []);

  const handleRouteToCData = useCallback(() => {
    setRouteToCampaigns(false);
  }, []);

  const handleBackToCampaigns = useCallback(() => {
    setRouteToMM(false);
    setRouteToCampaigns(true);
  }, []);

  const handleBackToPages = useCallback(() => {
    navigate("/existingbroadcastpages");
  }, [navigate]);

  const handleSaveCampaign = useCallback((newCampaign: Campaign) => {
    setCampaigns((prev) => {
      const existingIndex = prev.findIndex(
        (campaign) =>
          campaign.messageCampaignId === newCampaign.messageCampaignId
      );

      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = newCampaign;
        return updated;
      }

      return [...prev, newCampaign];
    });
  }, []);

  return (
    <Box
      className="content-page"
      sx={{
        ml: sideBar ? "250px" : "120px",
        backgroundColor: "#fff",
        mr: 10,
      }}>
      <Box className="content">
        {routeToCampaigns && !routeToMM && (
          <Grid container spacing={2} mt={1}>
            <Grid item xs={4} mt={2}>
              <Button
                variant="outlined"
                sx={{ textTransform: "none", ml: 16 }}
                onClick={handleBackToPages}>
                ← Back To Pages
              </Button>
            </Grid>
            <Grid item xs={8} mt={2}>
              <Typography
                variant="h5"
                fontWeight="bold"
                gutterBottom
                sx={{ textAlign: "start", ml: 10 }}>
                DM Campaigns
              </Typography>
            </Grid>
            <Grid container justifyContent="center">
              <Grid item xs={12} md={10} lg={8}>
                <SearchBarWithButton
                  placeholder="Search Campaign Name..."
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                  onNew={handleOpenCreate}
                  newBtnLabel="+ Create Campaign"
                />
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="center"
              className="custom-scrollbar-css"
              sx={{ height: "60vh" }}>
              <Grid item xs={12} md={10} lg={8}>
                <CampaignsList
                  campaigns={filteredCampaigns}
                  adAccountName={adAccountName}
                  adAccountCurrency={adAccountCurrency}
                  onOpenMessages={handleRouteToMM}
                  onOpenCampaignData={handleRouteToCData}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        <NewCampaignDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleSaveCampaign}
          editCampaign={editCampaign}
          fetchCampaigns={fetchCampaigns}
        />
      </Box>
      {routeToMM && (
        <MarketingMessages handleBackToCampaigns={handleBackToCampaigns} />
      )}
    </Box>
  );
};
export default CampaignsDashboard;
